import { useEffect, useRef } from "react";
import { cn } from "../../../lib/utils";
import ContentMarkdown from "../../../components/ContentMarkdown";
import {
  TYPEWRITER_SPEED,
  useTypewriter,
} from "../../../domains/gpt/hooks/useTypewriter";
import { GPT_ANSWER_CLASS } from "../../../domains/gpt/answer";

/**
 * The assistant "message" that opens each post-onboarding profiling turn. The
 * flow is a scripted conversation, but the message reveals with the SAME
 * typewriter effect and markdown rendering as a live JuisciGPT answer — it
 * reuses `useTypewriter` + `ContentMarkdown` (`GPT_ANSWER_CLASS` carries the
 * `.gpt-chat-answer.typing` caret) so it is visually identical to a real turn.
 *
 * `text` is a markdown string (bold `**…**`, `\n\n` paragraph breaks).
 *
 * `onDone` fires once when the typewriter finishes revealing `text` (null for
 * non-active turns). The conversation uses it to reveal that turn's composer
 * widget only after the message has typed, rather than at mount.
 *
 * `instant` skips the typewriter and shows `text` in full at once (still firing
 * `onDone` immediately). Used for turns restored from a reload — a resumed
 * thread's past bubbles must not re-type from scratch on mount.
 */
export function ConversationBubble({
  text,
  onDone,
  instant,
}: {
  text: string;
  onDone: (() => void) | null;
  instant: boolean;
}): React.JSX.Element {
  const reveal = useTypewriter(text, !instant, TYPEWRITER_SPEED.answer, 0);
  const firedRef = useRef(false);
  useEffect(() => {
    firedRef.current = false;
  }, [text]);
  useEffect(() => {
    if (!reveal.done || firedRef.current) return;
    firedRef.current = true;
    if (onDone !== null) onDone();
  }, [reveal.done, onDone]);
  return (
    <ContentMarkdown
      className={cn(
        "self-start",
        GPT_ANSWER_CLASS,
        reveal.done ? "" : "typing"
      )}
      math={false}
      linkVariant="anchor"
    >
      {reveal.text}
    </ContentMarkdown>
  );
}
